import { BrowserRouter as Router, Routes, Route, useLocation, matchRoutes } from 'react-router-dom';
import { useEffect, useRef, lazy, Suspense } from 'react';
import routes from './routes/Routes';
import fetchUserCountry from './api/getUserCountry';
import { useDeviceToken } from './hooks/useDeviceToken';
import { getFirebaseConfig, forceFirebaseEnvironment, setupTokenRefreshListener, diagnoseFirebaseTokenIssue } from './config/firebase';
import { UnreadCountProvider } from './contexts/UnreadCountContext';
import { AdvertDetailModalProvider, useAdvertDetailModal } from './contexts/AdvertDetailModalContext';
import { VoiceCallProvider } from './contexts/VoiceCallContext';
import { ToastProvider } from './components/push_notification/ToastProvider';
import { ChinaCartProvider } from './contexts/ChinaCartContext';
import { TariffProvider } from './contexts/TariffContext';
import { setupApiInterceptor } from './utils/apiInterceptor';
import { chatCache } from './app/profile/utils/chatCache';
import { prefetchProfileData } from './utils/prefetchProfileData';
import monitoringService from './services/monitoringService';
import { loadGoogleAnalytics, trackPageView } from './utils/googleAnalytics';
import { initMetrika, trackPageView as trackMetrikaPageView } from './utils/yandexMetrika';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { setHTMLLang } from './utils/seo';

const CallOverlay = lazy(() => import('./components/voice_call/CallOverlay'));
const PushNotificationOverlay = lazy(() => import('./components/push_notification/PushNotificationOverlay'));
const AppChatToastListener = lazy(() => import('./components/push_notification/AppChatToastListener'));
const ChinaCartMount = lazy(() => import('./app/china/components/ChinaCartMount'));
const TariffLimitModal = lazy(() => import('./components/TariffLimitModal'));
const AdvertDetailModal = lazy(() => import('./app/modals/advert_detail_modal/ui/Main'));

fetchUserCountry();

// Initialize Google Analytics on app start
loadGoogleAnalytics();

// Initialize Yandex Metrika on app start
initMetrika();

// Router observer component to track page views
function RouterObserver() {
  const location = useLocation();
  const lastMonitoringPage = useRef(null);

  useEffect(() => {
    const pageName = location.pathname;

    setHTMLLang(i18next.language);

    // Monitoring needs the route pattern (/announcement/:slugOrId), not the concrete id;
    // hash tabs (/profile#wallet) count as separate screens
    const matched = matchRoutes(routes, location);
    const tab = /^#[\w-]+$/.test(location.hash) ? location.hash : '';
    const monitoringPage = (matched?.[matched.length - 1].route.path || pageName) + tab;
    if (monitoringPage !== lastMonitoringPage.current) {
      lastMonitoringPage.current = monitoringPage;
      monitoringService.trackPageView(monitoringPage).catch(() => {});
    }

    // Track page view in Google Analytics
    trackPageView(pageName + location.search, {
      page_title: document.title,
    });

    // Track page view in Yandex Metrika
    trackMetrikaPageView(pageName + location.search);
  }, [location]);

  return null;
}

function App() {
  const { registerDevice, updateFirebaseToken, resetDeviceRegistration } = useDeviceToken();
  const { i18n } = useTranslation();

  // Initialize monitoring service
  useEffect(() => {
    // Setup API interceptor
    setupApiInterceptor();

    // Track app open
    monitoringService.trackAppOpen().catch(() => {});

    // Try to fetch user info for monitoring
    const accessToken = localStorage.getItem('access_token');
    let idleHandle = null;
    if (accessToken) {
      // Бірінші paint-пен бәсекелеспеуі үшін браузер бос болғанда ғана.
      const warmUp = () => {
        monitoringService.fetchUserPhoneNumber().catch(() => {});
        // Warm profile caches in the background so profile tabs open instantly.
        prefetchProfileData();
      };
      idleHandle = window.requestIdleCallback
        ? window.requestIdleCallback(warmUp, { timeout: 3000 })
        : window.setTimeout(warmUp, 1500);
    }

    // Tab hidden / closed → appClose (flushed via sendBeacon); back to visible → appOpen
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') monitoringService.trackAppClose().catch(() => {});
      else monitoringService.trackAppOpen().catch(() => {});
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (idleHandle !== null) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
        else window.clearTimeout(idleHandle);
      }
    };
  }, []);

  // Register device when app starts and try to get FCM token
  useEffect(() => {
    // Check if user is logged in
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return;
    }

    const isDeviceRegistered = localStorage.getItem('device_registered') === 'true';

    // Try to get FCM token and register/update device
    const tryRegisterWithToken = async () => {
      try {
        const { getFirebaseToken } = await import('./utils/firebaseUtils');
        const token = await getFirebaseToken();

        if (!token) {
          // No token yet; useDeviceToken blocks empty-token POSTs.
          // Firebase token refresh listener will retry when token becomes available.
          return;
        }

        if (isDeviceRegistered) {
          // Update token for existing device
          await updateFirebaseToken(token);
        } else {
          // Register new device with token
          await registerDevice(token);
        }
      } catch (error) {
        // Failed to get FCM token; token refresh listener will retry later
      }
    };

    tryRegisterWithToken();
  }, [registerDevice, updateFirebaseToken]);

  // Listen for login events to register device
  useEffect(() => {
    const handleLogin = async () => {
      // Кіру сәтінде access_token сақталды — socket-ті жаңа токенмен қайта құрамыз.
      // Болмаса app-boot кезінде токенсіз init болған socket сақталып, чат
      // серверге бетті жаңартқанша қосылмайды.
      try {
        const { forceReconnectSocket } = await import('./app/profile/utils/socketUtils');
        forceReconnectSocket();
      } catch (e) {
        // socket қайта құру сәтсіз болса — кейін reconnect әрекеті бар
      }

      try {
        const { getFirebaseToken } = await import('./utils/firebaseUtils');
        const token = await getFirebaseToken();

        if (!token) {
          return;
        }

        const isDeviceRegistered = localStorage.getItem('device_registered') === 'true';
        if (isDeviceRegistered) {
          await updateFirebaseToken(token);
        } else {
          await registerDevice(token);
        }
      } catch (error) {
        // Failed to get FCM token after login; token refresh listener will retry
      }
    };

    // Listen for custom login event
    window.addEventListener('userLogin', handleLogin);

    return () => {
      window.removeEventListener('userLogin', handleLogin);
    };
  }, [registerDevice, updateFirebaseToken]);

  // Logout кезінде socket-ті өшіреміз. Бұрын /profile-тен шыққанда disconnect
  // болатын; енді socket app бойынша тірі қалғандықтан, logout-та өшіру керек —
  // болмаса жарамсыз токенмен қалған socket байланып тұра береді. handleLogout
  // (ModalList.js) localStorage.clear()-дан БҰРЫН dispatch қылады, реттілік орынды.
  useEffect(() => {
    const handleLogout = async () => {
      chatCache.clearAll();
      try {
        const { disconnectSocket } = await import('./app/profile/utils/socketUtils');
        disconnectSocket();
      } catch {
        // socket өшіру сәтсіз болса — еншімізде емес
      }
    };
    window.addEventListener('userLogout', handleLogout);
    return () => window.removeEventListener('userLogout', handleLogout);
  }, []);

  // Setup FCM token refresh listener
  useEffect(() => {
    // Only set up if user is logged in
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return;
    }

    // Set up token refresh listener asynchronously
    // When Firebase refreshes the token, update it in backend
    let isMounted = true;
    let refreshListener = null;
    
    setupTokenRefreshListener((newToken) => {
      if (newToken && isMounted) {
        updateFirebaseToken(newToken);
      }
    }).then((result) => {
      refreshListener = result;
    }).catch((error) => {
      // Failed to set up token refresh listener
    });

    return () => {
      isMounted = false;
      // Cleanup interval if listener was set up
      if (refreshListener && refreshListener.cleanup) {
        refreshListener.cleanup();
      }
    };
  }, [updateFirebaseToken]);

  // Expose functions globally for testing
  useEffect(() => {
    window.updateFirebaseToken = updateFirebaseToken;
    window.resetDeviceRegistration = resetDeviceRegistration;

    // Add function to clear device registration state
    window.clearDeviceRegistration = () => {
      localStorage.removeItem('device_registered');
      localStorage.removeItem('device_id');
    };

    // Add Firebase environment testing functions
    window.getFirebaseConfig = getFirebaseConfig;
    window.forceFirebaseEnvironment = forceFirebaseEnvironment;
    window.testFirebaseConfig = () => {
      return {
        current: getFirebaseConfig(),
        dev: getFirebaseConfig('dev'),
        prod: getFirebaseConfig('prod')
      };
    };

    // Test Firebase token generation
    window.testFirebaseToken = async () => {
      try {
        const { getFirebaseToken } = await import('./utils/firebaseUtils');
        const token = await getFirebaseToken();
        return token;
      } catch (error) {
        return null;
      }
    };

    // Check Firebase SDK loading
    window.checkFirebaseSDK = () => {
      return {
        sdkLoaded: !!window.firebase,
        appsCount: window.firebase?.apps?.length || 0,
        messagingAvailable: !!window.firebase?.messaging
      };
    };

    // Diagnose why Firebase token might be empty
    window.diagnoseFirebaseToken = async () => {
      const diagnostics = await diagnoseFirebaseTokenIssue();
      return diagnostics;
    };

    // Force get Firebase token and register/update device
    window.forceGetFirebaseToken = async () => {
      const { getFirebaseToken } = await import('./utils/firebaseUtils');
      const token = await getFirebaseToken();

      if (token) {
        const isDeviceRegistered = localStorage.getItem('device_registered') === 'true';

        if (isDeviceRegistered) {
          await updateFirebaseToken(token);
        } else {
          await registerDevice(token);
        }

        return token;
      }

      return null;
    };

    return () => {
      delete window.updateFirebaseToken;
      delete window.resetDeviceRegistration;
      delete window.clearDeviceRegistration;
      delete window.getFirebaseConfig;
      delete window.forceFirebaseEnvironment;
      delete window.testFirebaseConfig;
      delete window.testFirebaseToken;
      delete window.checkFirebaseSDK;
      delete window.diagnoseFirebaseToken;
      delete window.forceGetFirebaseToken;
    };
  }, [updateFirebaseToken, resetDeviceRegistration, registerDevice]);

  return (
    <div className="bg-secondary min-h-screen">
      <AdvertDetailModalProvider>
        <UnreadCountProvider>
          <VoiceCallProvider>
            <ChinaCartProvider>
              <TariffProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <RouterObserver />
                <ToastProvider>
                {/* Көп компонент t-ны i18next-тен тікелей алады — тіл ауысқанда
                    бетті қайта жүктемей, беттер ағашын қайта mount қыламыз. */}
                <Routes key={i18n.language}>
                  {routes.map(({ path, element }, index) => (
                    <Route key={index} path={path} element={element} />
                  ))}
                </Routes>
                <Suspense fallback={null}>
                  <AdvertDetailModalWrapper />
                </Suspense>
                <Suspense fallback={null}>
                  <CallOverlay />
                  <PushNotificationOverlay />
                  <AppChatToastListener />
                  <ChinaCartMount />
                  <TariffLimitModal />
                </Suspense>
                </ToastProvider>
              </Router>
            </TariffProvider>
            </ChinaCartProvider>
          </VoiceCallProvider>
        </UnreadCountProvider>
      </AdvertDetailModalProvider>
    </div>
  );
}

function AdvertDetailModalWrapper() {
  const { selectedAdvertId, closeAdvert } = useAdvertDetailModal();
  if (!selectedAdvertId) return null;
  return (
    <AdvertDetailModal
      isOpen={!!selectedAdvertId}
      advertId={selectedAdvertId}
      onClose={closeAdvert}
    />
  );
}

export default App;